import React from 'react'

import { DatePicker, formatDateValue } from '@components/datePicker'
import { InputField } from '@components/inputField'
import {
  ReservationGridLayout,
  ReservationGridLayoutRow,
} from '@components/reservationGrid/shared/reservationGridLayout'
import { SelectField } from '@components/selectField'
import {
  BIRTHDAY_PARTY_STARTS,
  BirthdayAvailability,
  BirthdayRoom,
  BirthdaySelectionMode,
  BirthdaySession,
  birthdaySessionId,
} from '@facades/birthday'
import { getCzechCalendarDayInfo } from '@utils/czechCalendar'

import {
  AdjustChildrenButton,
  AllocationContainer,
  AllocationRow,
  AllocationTitle,
  GuestFields,
  LoadingMessage,
  MissingPlayerSelection,
  PlayerCountButton,
  PlayerCountOptions,
  PublicTimeSlot,
  QuestLabel,
  QuestThumbnail,
  ReservedSelectionNotice,
  ReservationSection,
  TimeSlotLegend,
  ValidationMessage,
} from './birthdayReservationGrid.styled'

function availabilityLabel(reason: string | null) {
  switch (reason) {
    case 'age_restriction':
      return 'Od 13 let'
    case 'standard_slot_missing':
      return 'Mimo rozvrh'
    case 'standard_slot_blocked':
      return 'Blokováno · lze rezervovat'
    case 'standard_slot_occupied':
    case 'game_interval_conflict':
      return 'Obsazeno'
    default:
      return 'Volné'
  }
}

type BirthdayReservationGridProps = {
  rooms: BirthdayRoom[]
  availability: Record<string, BirthdayAvailability | undefined>
  isLoading: boolean
  isError: boolean
  date: string
  selectionMode: BirthdaySelectionMode
  age: number
  children: number
  parents: number
  selectedSessions: BirthdaySession[]
  preservedSessionIds?: string[]
  hasReservedSelection?: boolean
  assignments: Record<string, number>
  supervision: Record<string, 'parent' | 'coach' | 'none'>
  assignedChildren: number
  allocationValid: boolean
  onDateChange: (value: string) => void
  onAgeChange: (value: number) => void
  onChildrenChange: (value: number) => void
  onParentsChange: (value: number) => void
  onSlotToggle: (roomId: string, start: string) => void
  onAssignmentChange: (roomId: string, value: number) => void
  onSupervisionChange: (
    roomId: string,
    value: 'parent' | 'coach' | 'none'
  ) => void
}

const RoomLabel = ({ room }: { room: BirthdayRoom }) => (
  <QuestLabel>
    <QuestThumbnail src={room.image} alt={room.name} loading="lazy" />
    <span>
      <strong>{room.name}</strong>
      <small>
        Max. {room.capacity} dětí
        {room.minAge ? ' · od 13 let' : ''}
      </small>
    </span>
  </QuestLabel>
)

const minimumBirthdayDate = formatDateValue(new Date())

export const BirthdayReservationGrid = ({
  rooms,
  availability,
  isLoading,
  isError,
  date,
  selectionMode,
  age,
  children,
  parents,
  selectedSessions,
  preservedSessionIds = [],
  hasReservedSelection = false,
  assignments,
  supervision,
  assignedChildren,
  allocationValid,
  onDateChange,
  onAgeChange,
  onChildrenChange,
  onParentsChange,
  onSlotToggle,
  onAssignmentChange,
  onSupervisionChange,
}: BirthdayReservationGridProps) => (
  <ReservationSection>
    <h2>1. Vyberte termín a hry</h2>
    <span>
      {selectionMode === 'parallel'
        ? 'Vyberte jeden čas příchodu a rozdělte skupinu mezi souběžné hry.'
        : 'Vyberte více časů pro skupiny, které se k oslavě připojí postupně.'}
    </span>
    <GuestFields>
      <DatePicker
        label="Datum oslavy"
        value={date}
        min={minimumBirthdayDate}
        getDayInfo={getCzechCalendarDayInfo}
        onChange={onDateChange}
      />
      <InputField
        label="Celkový počet hrajících dětí"
        type="number"
        value={children}
        min={1}
        max={30}
        onChange={(event) => onChildrenChange(Number(event.target.value))}
      />
      <InputField
        label="Věk nejmladšího hráče"
        type="number"
        value={age}
        min={1}
        max={18}
        onChange={(event) => onAgeChange(Number(event.target.value))}
      />
      <InputField
        label="Počet doprovázejících rodičů"
        type="number"
        value={parents}
        min={0}
        max={30}
        onChange={(event) => onParentsChange(Number(event.target.value))}
      />
    </GuestFields>
    {isLoading && <LoadingMessage>Ověřujeme celý den…</LoadingMessage>}
    {isError && (
      <ValidationMessage>
        Dostupnost se nepodařilo načíst. Zkuste to znovu.
      </ValidationMessage>
    )}
    {hasReservedSelection && (
      <ReservedSelectionNotice>
        <strong>Uložená rezervace:</strong> zlatě označené termíny jsou již
        rezervované touto objednávkou a nelze je znovu vybrat.
      </ReservedSelectionNotice>
    )}
    <TimeSlotLegend aria-label="Vysvětlení dostupnosti termínů">
      <span>
        <i aria-hidden="true" />
        Volné
      </span>
      <span>
        <i aria-hidden="true" />
        Vybráno
      </span>
      <span>
        <i aria-hidden="true" />
        Nedostupné
      </span>
    </TimeSlotLegend>
    <ReservationGridLayout forceDesktopLayout>
      {rooms.map((room) => (
        <ReservationGridLayoutRow
          key={room.id}
          forceDesktopLayout
          label={<RoomLabel room={room} />}
        >
          {BIRTHDAY_PARTY_STARTS.map((partyStart) => {
            const result = availability[partyStart]?.rooms.find(
              (item) => item.roomId === room.id
            )
            const available = Boolean(result?.available)
            const selected = selectedSessions.some(
              (session) => session.id === birthdaySessionId(room.id, partyStart)
            )
            const sessionId = birthdaySessionId(room.id, partyStart)
            const reservedSelection =
              selected && !available && preservedSessionIds.includes(sessionId)
            const status = reservedSelection
              ? 'Rezervováno'
              : selected
              ? 'Vybráno'
              : result
              ? availabilityLabel(result.reason)
              : 'Nedostupné'

            return (
              <PublicTimeSlot
                key={`${room.id}-${partyStart}`}
                type="button"
                selected={selected}
                unavailable={!available}
                reservedSelection={reservedSelection}
                disabled={!available}
                aria-pressed={selected}
                aria-label={`${room.name}, příchod ${partyStart}, ${status}`}
                onClick={() => onSlotToggle(room.id, partyStart)}
              >
                {reservedSelection && (
                  <i className="fa fa-lock reserved-lock" aria-hidden="true" />
                )}
                <strong>{partyStart}</strong>
                <small>{status}</small>
              </PublicTimeSlot>
            )
          })}
        </ReservationGridLayoutRow>
      ))}
    </ReservationGridLayout>
    {selectedSessions.length > 0 && (
      <AllocationContainer aria-label="Výběr počtu hráčů">
        <AllocationTitle>
          <strong>2. Vyberte počet hráčů pro každou hru</strong>
          <span>
            {selectionMode === 'parallel'
              ? `Příchod ${selectedSessions[0].start}, začátek her ${
                  availability[selectedSessions[0].start]?.rooms[0]
                    ?.gameStart ?? '—'
                }.`
              : 'Každý řádek představuje jednu skupinu a její čas příchodu.'}{' '}
            Rozdělte všech {children} dětí.
          </span>
        </AllocationTitle>
        {selectedSessions.map((session) => {
          const room = rooms.find((item) => item.id === session.roomId)
          if (!room) return null
          const selectedPlayers = assignments[session.id]
          const rowLabel =
            selectionMode === 'staggered'
              ? `${room.name}, příchod ${session.start}`
              : room.name

          return (
            <AllocationRow key={session.id}>
              <div>
                <strong>{rowLabel}</strong>
                {!selectedPlayers && (
                  <MissingPlayerSelection>
                    Vyberte počet hráčů.
                  </MissingPlayerSelection>
                )}
              </div>
              <PlayerCountOptions aria-label={`Počet hráčů: ${rowLabel}`}>
                {Array.from({ length: room.capacity }, (_, index) => {
                  const count = index + 1
                  return (
                    <PlayerCountButton
                      key={count}
                      type="button"
                      selected={selectedPlayers === count}
                      aria-pressed={selectedPlayers === count}
                      aria-label={`${rowLabel}: ${count} hráčů`}
                      onClick={() => onAssignmentChange(session.id, count)}
                    >
                      {count}
                    </PlayerCountButton>
                  )
                })}
              </PlayerCountOptions>
              <SelectField
                label={
                  selectionMode === 'staggered'
                    ? `Doprovod: ${rowLabel}`
                    : 'Doprovod'
                }
                value={
                  supervision[session.id] ?? (age < 13 ? 'parent' : 'none')
                }
                onChange={(event) =>
                  onSupervisionChange(
                    session.id,
                    event.target.value as 'parent' | 'coach' | 'none'
                  )
                }
              >
                <option value="parent">Vlastní dospělý</option>
                <option value="coach">Placený kouč</option>
                {age >= 13 && <option value="none">Bez doprovodu</option>}
              </SelectField>
            </AllocationRow>
          )
        })}
      </AllocationContainer>
    )}
    {!allocationValid && selectedSessions.length > 0 && (
      <ValidationMessage role="alert">
        <span>
          Ve hrách je rozděleno {assignedChildren} z {children} dětí. Vyberte
          počet hráčů pro každou zvolenou hru.
        </span>
        {assignedChildren > 0 && assignedChildren !== children && (
          <AdjustChildrenButton
            type="button"
            onClick={() => onChildrenChange(assignedChildren)}
          >
            Upravit celkový počet na {assignedChildren}
          </AdjustChildrenButton>
        )}
      </ValidationMessage>
    )}
  </ReservationSection>
)
